import { EventData } from '../models/manager-content-vehicle-history-drawer.model';

/**
 * Interfaz para los parámetros parseados del evento
 */
export interface ParsedEventParams {
  // ✅ TODOS los parámetros son strings para compatibilidad total con código antiguo

  // Parámetros básicos
  gps?: string;
  event?: string;
  deviceId?: string;
  eventId?: string;
  eventDateTime?: string;
  driverId?: string;
  tripId?: string;

  // Coordenadas
  latitude?: string;
  longitude?: string;
  headingAngle?: string;
  locationQuality?: string;
  speed?: string;

  // Parámetros de potencia
  extPower?: string;
  internalBatteryPower?: string;

  // Estados del dispositivo
  GPO?: string;
  di4?: string;
  sat?: string;

  // Parámetros multimedia
  has_video?: string; // "1" o "0"
  has_image?: string; // "1" o "0"
  cabin_video?: string; // "1" o "0"
  road_video?: string; // "1" o "0"
  cabin_image?: string; // "1" o "0"
  road_image?: string; // "1" o "0"

  // Atributos del evento
  eventAttr?: string;

  // Propiedades adicionales (cualquier otra que pueda venir)
  [key: string]: any;
}

/**
 * Función para parsear los parámetros del evento desde string a objeto
 * @param eventString String de parámetros del evento (formato: "key1=value1|key2=value2")
 * @returns Objeto con los parámetros parseados y tipados
 */
export function parseEventParams(eventString: string): ParsedEventParams {
  if (!eventString || typeof eventString !== 'string') {
    return {};
  }

  const params: ParsedEventParams = {};

  // Dividir por el separador '|'
  const pairs = eventString.split('|');

  for (const pair of pairs) {
    if (!pair || !pair.includes('=')) continue;

    const [key, value] = pair.split('=');
    if (!key || value === undefined) continue;

    const cleanKey = key.trim();
    const cleanValue = value.trim();

    // Convertir valores según el tipo esperado
    params[cleanKey] = cleanValue;
  }

  return params;
}

/**
 * Función para obtener parámetros multimedia específicos
 * @param event Evento del historial
 * @returns Objeto con información multimedia o null si no hay multimedia
 */
export function getMultimediaInfo(event: EventData): {
  hasVideo: boolean;
  hasImage: boolean;
  videoSource: 'CABIN' | 'ROAD' | 'BOTH' | null;
  imageSource: 'CABIN' | 'ROAD' | 'BOTH' | null;
  eventId: string;
  deviceId: string;
  canRequestVideo: boolean;
  canRequestImage: boolean;
} | null {
  if (!event.parametros) {
    return null;
  }

  const params = parseEventParams(event.parametros);

  // ✅ Extraer valores para video (convertir strings "1"/"0" a booleanos)
  const hasVideo = params.has_video === '1';
  const cabinVideo = params.cabin_video === '1';
  const roadVideo = params.road_video === '1';

  // ✅ Extraer valores para imagen (convertir strings "1"/"0" a booleanos)
  const hasImage = params.has_image === '1';
  const cabinImage = params.cabin_image === '1';
  const roadImage = params.road_image === '1';

  // Determinar fuente del video
  let videoSource: 'CABIN' | 'ROAD' | 'BOTH' | null = null;
  if (hasVideo) {
    if (cabinVideo && roadVideo) {
      videoSource = 'BOTH';
    } else if (cabinVideo) {
      videoSource = 'CABIN';
    } else if (roadVideo) {
      videoSource = 'ROAD';
    }
  }

  // Determinar fuente de la imagen
  let imageSource: 'CABIN' | 'ROAD' | 'BOTH' | null = null;
  if (hasImage) {
    if (cabinImage && roadImage) {
      imageSource = 'BOTH';
    } else if (cabinImage) {
      imageSource = 'CABIN';
    } else if (roadImage) {
      imageSource = 'ROAD';
    }
  }

  // Obtener eventId y deviceId
  const eventId = params.eventId || '';
  const deviceId = params.deviceId || '';

  return {
    hasVideo,
    hasImage,
    videoSource,
    imageSource,
    eventId,
    deviceId,
    canRequestVideo: hasVideo && videoSource !== null,
    canRequestImage: hasImage && imageSource !== null,
  };
}

/**
 * Función para verificar si un evento tiene multimedia disponible
 * @param event Evento del historial
 * @returns true si el evento tiene multimedia disponible
 */
export function hasMultimediaAvailable(event: EventData): boolean {
  const multimediaInfo = getMultimediaInfo(event);
  return multimediaInfo
    ? multimediaInfo.canRequestVideo || multimediaInfo.canRequestImage
    : false;
}

/**
 * Función para obtener parámetros específicos del evento
 * @param event Evento del historial
 * @param keys Array de claves de parámetros a extraer
 * @returns Objeto con solo los parámetros solicitados
 */
export function getSpecificParams(
  event: EventData,
  keys: string[]
): Partial<ParsedEventParams> {
  if (!event.parametros) {
    return {};
  }

  const allParams = parseEventParams(event.parametros);
  const specificParams: Partial<ParsedEventParams> = {};

  keys.forEach((key) => {
    if (allParams.hasOwnProperty(key)) {
      specificParams[key] = allParams[key];
    }
  });

  return specificParams;
}

/**
 * Función para generar URLs de multimedia basadas en los parámetros
 * @param event Evento del historial
 * @param baseUrl URL base de la API
 * @returns Objeto con URLs de video e imagen
 */
export function generateMultimediaUrls(
  event: EventData,
  baseUrl: string
): {
  videoUrl?: string;
  imageUrl?: string;
  deviceId: string;
  eventId: string;
  videoSource: 'CABIN' | 'ROAD' | 'BOTH' | null;
  imageSource: 'CABIN' | 'ROAD' | 'BOTH' | null;
} | null {
  const multimediaInfo = getMultimediaInfo(event);
  if (!multimediaInfo) {
    return null;
  }

  const { deviceId, eventId, videoSource, imageSource } = multimediaInfo;

  if (!deviceId || !eventId) {
    return null;
  }

  // Para video, usar la fuente determinada
  let videoUrl: string | undefined;
  if (videoSource === 'BOTH') {
    // Si es BOTH, generar URL para CABIN por defecto
    videoUrl = `${baseUrl}/api/media/video/${deviceId}/${eventId}/CABIN/0`;
  } else if (videoSource) {
    videoUrl = `${baseUrl}/api/media/video/${deviceId}/${eventId}/${videoSource}/0`;
  }

  // Para imagen, usar la fuente determinada
  let imageUrl: string | undefined;
  if (imageSource === 'BOTH') {
    // Si es BOTH, generar URL para CABIN por defecto
    imageUrl = `${baseUrl}/api/media/image/${deviceId}/${eventId}/CABIN/0`;
  } else if (imageSource) {
    imageUrl = `${baseUrl}/api/media/image/${deviceId}/${eventId}/${imageSource}/0`;
  }

  return {
    videoUrl,
    imageUrl,
    deviceId,
    eventId,
    videoSource,
    imageSource,
  };
}
